<script setup>
// A lista de campanhas na planta do kit (Outbound › Messages): "N campanhas" e uma tabela. O título é a coluna fixa;
// no chat ao vivo abre a gaveta de edição, em SMS e WhatsApp não abre nada (o woot só sabe excluir, 8-3 = A).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useDateLocale } from 'dashboard/composables/useDateLocale';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { Edit16, TrashCan16 } from '@carbon/icons-vue';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import { initials } from 'dashboard/components-next/polaris/cards/initials';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from 'dashboard/components-next/polaris/ui/avatar';
import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'dashboard/components-next/polaris/ui/table';

const props = defineProps({
  campaigns: {
    type: Array,
    required: true,
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  // O desenho esmaecido do vazio: no lugar da contagem, diz que são campanhas de exemplo.
  example: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();
const dateLocale = useDateLocale();

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';

const columns = computed(() => {
  const labels = {
    TITLE: t('CAMPAIGN.TABLE.COLUMNS.TITLE'),
    STATUS: t('CAMPAIGN.TABLE.COLUMNS.STATUS'),
    MESSAGE: t('CAMPAIGN.TABLE.COLUMNS.MESSAGE'),
    SENDER: t('CAMPAIGN.TABLE.COLUMNS.SENDER'),
    INBOX: t('CAMPAIGN.TABLE.COLUMNS.INBOX'),
    SCHEDULED_AT: t('CAMPAIGN.TABLE.COLUMNS.SCHEDULED_AT'),
  };
  const keys = props.isLiveChatType
    ? ['TITLE', 'STATUS', 'MESSAGE', 'SENDER', 'INBOX']
    : ['TITLE', 'STATUS', 'MESSAGE', 'INBOX', 'SCHEDULED_AT'];
  return keys.map(key => ({ key, label: labels[key] }));
});

const statusOf = campaign => {
  if (props.isLiveChatType) {
    return campaign.enabled
      ? {
          label: t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED'),
          variant: 'success',
        }
      : {
          label: t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED'),
          variant: 'secondary',
        };
  }
  if (campaign.campaign_status === STATUS_COMPLETED) {
    return {
      label: t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED'),
      variant: 'secondary',
    };
  }
  if (campaign.campaign_status === STATUS_PROCESSING) {
    return {
      label: t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING'),
      variant: 'warning',
    };
  }
  return { label: t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED'), variant: 'info' };
};

const senderName = campaign =>
  campaign.sender?.name || t('CAMPAIGN.LIVE_CHAT.CARD.CAMPAIGN_DETAILS.BOT');

const inboxIcon = campaign => {
  const { medium, channel_type: type } = campaign.inbox || {};
  return getInboxIconByType(type, medium);
};

// O formato do cartão do woot ("Sep 29, 2:13 PM"), no idioma da tela ("29 set, 14:13"); de outro ano, com o ano.
const scheduledLabel = campaign =>
  messageTimestamp(campaign.scheduled_at, 'LLL d, h:mm a', dateLocale.value);

const menuItems = computed(() => [
  ...(props.isLiveChatType
    ? [{ label: t('CAMPAIGN.TABLE.EDIT'), value: 'edit', icon: Edit16 }]
    : []),
  {
    label: t('CAMPAIGN.CONFIRM_DELETE.CONFIRM'),
    value: 'delete',
    icon: TrashCan16,
    destructive: true,
  },
]);

const handleAction = (campaign, action) => {
  if (action === 'edit') emit('edit', campaign);
  if (action === 'delete') emit('delete', campaign);
};

// A primeira coluna é a fixa: acompanha a rolagem para o lado no celular.
const pinned = 'sticky start-0 z-10 bg-polaris-background';
</script>

<template>
  <div class="grid gap-4">
    <p
      v-if="!isLoading"
      class="type-polaris-section-title"
      aria-live="polite"
      data-campaign-count
    >
      {{
        example
          ? t('CAMPAIGN.LIST.EXAMPLE')
          : t('CAMPAIGN.LIST.COUNT', { count: campaigns.length })
      }}
    </p>
    <div
      v-else
      class="grid gap-2"
      role="status"
      :aria-label="t('CAMPAIGN.LIST.LOADING')"
    >
      <Skeleton class="h-6 w-32" />
      <Skeleton v-for="row in 5" :key="row" class="h-12 w-full" />
    </div>
    <!-- A tabela do kit rola para os lados dentro de si mesma; a região recebe o foco para o teclado rolar. -->
    <Table
      v-if="!isLoading"
      tabindex="0"
      role="region"
      :aria-label="t('CAMPAIGN.TABLE.LABEL')"
    >
      <TableCaption class="sr-only">{{ title }}</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead
            v-for="(column, at) in columns"
            :key="column.key"
            :class="at ? '' : pinned"
          >
            {{ column.label }}
          </TableHead>
          <TableHead class="w-12">
            <span class="sr-only">{{ t('CAMPAIGN.TABLE.EDIT') }}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow
          v-for="campaign in campaigns"
          :key="campaign.id"
          :data-campaign-id="campaign.id"
        >
          <TableCell
            class="min-w-44 max-w-64 whitespace-normal font-medium"
            :class="pinned"
          >
            <Button
              v-if="isLiveChatType"
              type="button"
              variant="link"
              size="sm"
              class="h-auto min-h-6 min-w-0 whitespace-normal p-0 text-start font-medium [overflow-wrap:anywhere]"
              :data-campaign-title="campaign.id"
              @click="emit('edit', campaign)"
            >
              {{ campaign.title }}
            </Button>
            <span v-else class="[overflow-wrap:anywhere]">
              {{ campaign.title }}
            </span>
          </TableCell>
          <TableCell>
            <Badge :variant="statusOf(campaign).variant">
              {{ statusOf(campaign).label }}
            </Badge>
          </TableCell>
          <TableCell class="min-w-40 max-w-64">
            <div
              v-dompurify-html="
                formatMessage(campaign.message, false, false, false)
              "
              class="line-clamp-1 [&>p]:mb-0"
              :title="campaign.message"
            />
          </TableCell>
          <TableCell v-if="isLiveChatType">
            <div class="flex items-center gap-2">
              <Avatar class="size-6" aria-hidden="true">
                <AvatarImage
                  v-if="campaign.sender?.thumbnail"
                  :src="campaign.sender.thumbnail"
                  :alt="senderName(campaign)"
                />
                <AvatarFallback class="text-xs font-medium">
                  {{ initials(senderName(campaign)) }}
                </AvatarFallback>
              </Avatar>
              <span class="max-w-32 truncate" :title="senderName(campaign)">
                {{ senderName(campaign) }}
              </span>
            </div>
          </TableCell>
          <TableCell>
            <div class="flex items-center gap-2">
              <Icon
                :icon="inboxIcon(campaign)"
                class="size-4 shrink-0 text-polaris-muted-foreground"
              />
              <span class="max-w-48 truncate" :title="campaign.inbox?.name">
                {{ campaign.inbox?.name }}
              </span>
            </div>
          </TableCell>
          <TableCell
            v-if="!isLiveChatType"
            class="text-polaris-muted-foreground"
          >
            <time
              :datetime="new Date(campaign.scheduled_at * 1000).toISOString()"
            >
              {{ scheduledLabel(campaign) }}
            </time>
          </TableCell>
          <TableCell class="w-12 text-end">
            <RowMenu
              :label="t('CAMPAIGN.TABLE.ACTIONS', { title: campaign.title })"
              :items="menuItems"
              @action="action => handleAction(campaign, action)"
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</template>
